/**
 * AnalyticsPanel — rich analytics with SVG charts + AI chat
 * Used in both BusinessDashboard (scope=restaurant) and PlatformAdminDashboard (scope=platform)
 *
 * Props:
 *   slug        — restaurant slug (restaurant scope only)
 *   scope       — 'restaurant' | 'platform'
 *   fetchData   — async fn(days) → analytics data
 *   aiAnalyze   — async fn(messages) → { reply }
 *   currencyCode— e.g. 'USD'
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';

// ── Tiny SVG chart helpers ─────────────────────────────────────────────────────
function LineChart({ data, color='#c8a96e', height=120, label='', valueKey='revenue', labelKey='_id' }) {
  if (!data?.length) return <div style={{height,display:'flex',alignItems:'center',justifyContent:'center',color:'rgba(255,255,255,.2)',fontSize:'.75rem'}}>No data</div>;
  const vals = data.map(d => Number(d[valueKey])||0);
  const max  = Math.max(...vals, 1);
  const min  = Math.min(...vals, 0);
  const range = max - min || 1;
  const w = 600, h = height;
  const pad = { t:10, b:28, l:44, r:10 };
  const iw  = w - pad.l - pad.r;
  const ih  = h - pad.t - pad.b;
  const pts = data.map((d, i) => {
    const x = pad.l + (i/(data.length-1||1))*iw;
    const y = pad.t + ih - ((Number(d[valueKey])||0) - min)/range * ih;
    return [x, y];
  });
  const path = pts.map((p,i) => `${i===0?'M':'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const area = path + ` L${pts[pts.length-1][0].toFixed(1)},${(pad.t+ih).toFixed(1)} L${pad.l},${(pad.t+ih).toFixed(1)} Z`;

  // Y axis labels
  const yTicks = [0,.25,.5,.75,1].map(t => ({ y: pad.t+ih - t*ih, val: min + t*range }));
  // X axis: show every Nth label
  const step = Math.max(1, Math.ceil(data.length/7));
  const xLabels = data.filter((_,i)=>i%step===0||i===data.length-1).map(d => String(d[labelKey]).slice(-5));

  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{width:'100%',height,display:'block'}}>
      <defs>
        <linearGradient id={`lg-${label}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3"/>
          <stop offset="100%" stopColor={color} stopOpacity="0.02"/>
        </linearGradient>
      </defs>
      {/* Grid lines */}
      {yTicks.map((t,i) => <line key={i} x1={pad.l} y1={t.y} x2={w-pad.r} y2={t.y} stroke="rgba(255,255,255,.06)" strokeWidth="1"/>)}
      {/* Area fill */}
      <path d={area} fill={`url(#lg-${label})`}/>
      {/* Line */}
      <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"/>
      {/* Dots */}
      {pts.map((p,i) => <circle key={i} cx={p[0]} cy={p[1]} r="3" fill={color} stroke="#111" strokeWidth="1.5"/>)}
      {/* Y labels */}
      {yTicks.map((t,i) => <text key={i} x={pad.l-5} y={t.y+4} textAnchor="end" fill="rgba(255,255,255,.3)" fontSize="9">{t.val>=1000?`${(t.val/1000).toFixed(1)}k`:t.val.toFixed(0)}</text>)}
      {/* X labels */}
      {data.filter((_,i)=>i%step===0||i===data.length-1).map((d,i)=>{
        const xi = data.indexOf(d);
        const xp = pad.l + (xi/(data.length-1||1))*iw;
        return <text key={i} x={xp} y={h-6} textAnchor="middle" fill="rgba(255,255,255,.25)" fontSize="9">{String(d[labelKey]).slice(-5)}</text>;
      })}
    </svg>
  );
}

function BarChart({ data, color='#a78bfa', height=120, valueKey='count', labelKey='_id', maxBars=10 }) {
  if (!data?.length) return <div style={{height,display:'flex',alignItems:'center',justifyContent:'center',color:'rgba(255,255,255,.2)',fontSize:'.75rem'}}>No data</div>;
  const trimmed = data.slice(0,maxBars);
  const max  = Math.max(...trimmed.map(d=>Number(d[valueKey])||0), 1);
  const w=600, h=height;
  const pad={t:8,b:30,l:36,r:8};
  const bw = (w-pad.l-pad.r)/trimmed.length;
  const gap = bw*0.18;
  const ih  = h-pad.t-pad.b;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{width:'100%',height,display:'block'}}>
      {trimmed.map((d,i)=>{
        const val  = Number(d[valueKey])||0;
        const barH = (val/max)*ih;
        const x    = pad.l + i*bw + gap/2;
        const y    = pad.t + ih - barH;
        return (
          <g key={i}>
            <rect x={x} y={y} width={bw-gap} height={Math.max(barH,2)} rx="3" fill={color} opacity="0.8"/>
            {barH>14 && <text x={x+(bw-gap)/2} y={y+13} textAnchor="middle" fill="#fff" fontSize="9" fontWeight="700">{val>=1000?`${(val/1000).toFixed(1)}k`:val}</text>}
            <text x={x+(bw-gap)/2} y={h-6} textAnchor="middle" fill="rgba(255,255,255,.3)" fontSize="9">{String(d[labelKey]).slice(0,8)}</text>
          </g>
        );
      })}
      <line x1={pad.l} y1={pad.t+ih} x2={w-pad.r} y2={pad.t+ih} stroke="rgba(255,255,255,.1)" strokeWidth="1"/>
    </svg>
  );
}

function DonutChart({ data, size=120, valueKey='count', labelKey='_id' }) {
  if (!data?.length) return null;
  const COLORS = ['#c8a96e','#a78bfa','#34d399','#60a5fa','#f97316','#f472b6','#facc15','#fb7185'];
  const total = data.reduce((s,d)=>s+(Number(d[valueKey])||0),0)||1;
  const r=42, cx=60, cy=60, stroke=18;
  let offset=0;
  const slices = data.map((d,i)=>{
    const pct = (Number(d[valueKey])||0)/total;
    const circ = 2*Math.PI*r;
    const dash = pct*circ;
    const s = { dash, gap:circ-dash, offset:circ-offset, color:COLORS[i%COLORS.length], label:d[labelKey], val:d[valueKey] };
    offset += dash;
    return s;
  });
  return (
    <div style={{display:'flex',alignItems:'center',gap:'1rem',flexWrap:'wrap'}}>
      <svg width={size} height={size} viewBox="0 0 120 120" style={{flexShrink:0}}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,.05)" strokeWidth={stroke}/>
        {slices.map((s,i)=>(
          <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={s.color} strokeWidth={stroke}
            strokeDasharray={`${s.dash} ${s.gap}`} strokeDashoffset={s.offset} transform="rotate(-90 60 60)"/>
        ))}
      </svg>
      <div style={{flex:1,display:'flex',flexDirection:'column',gap:'.25rem'}}>
        {slices.map((s,i)=>(
          <div key={i} style={{display:'flex',alignItems:'center',gap:'.4rem',fontSize:'.72rem'}}>
            <span style={{width:8,height:8,borderRadius:'50%',background:s.color,flexShrink:0}}/>
            <span style={{color:'rgba(255,255,255,.6)',flex:1}}>{s.label}</span>
            <span style={{color:s.color,fontWeight:700}}>{s.val}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function HeatmapRow({ data, label='Peak Hours' }) {
  if (!data?.length) return null;
  const hours = Array.from({length:24},(_,h)=>{ const d=data.find(x=>x._id===h); return { h, count: d?.count||0 }; });
  const max = Math.max(...hours.map(h=>h.count), 1);
  return (
    <div>
      <div style={{fontSize:'.65rem',color:'rgba(255,255,255,.3)',marginBottom:'.4rem',textTransform:'uppercase',letterSpacing:'.06em'}}>{label}</div>
      <div style={{display:'flex',gap:'2px',flexWrap:'nowrap',overflowX:'auto'}}>
        {hours.map(({h,count})=>{
          const intensity = count/max;
          return (
            <div key={h} title={`${h}:00 — ${count} orders`}
              style={{flex:'0 0 auto',width:'calc((100% - 48px)/24)',minWidth:14,height:28,borderRadius:3,
                background:`rgba(200,169,110,${intensity*0.85+0.03})`,
                display:'flex',alignItems:'flex-end',justifyContent:'center',paddingBottom:2}}>
              {h%4===0 && <span style={{fontSize:'8px',color:'rgba(255,255,255,.3)'}}>{h}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Stat Card ─────────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, sub, color='#c8a96e' }) {
  return (
    <div style={{background:'rgba(255,255,255,.03)',border:`1px solid ${color}22`,borderRadius:14,padding:'1rem',display:'flex',flexDirection:'column',gap:'.3rem',minWidth:0}}>
      <span style={{fontSize:'1.5rem'}}>{icon}</span>
      <span style={{fontSize:'1.35rem',fontWeight:900,color,fontFamily:"'Playfair Display',serif",overflowWrap:'anywhere',lineHeight:1.15}}>{value}</span>
      <span style={{fontSize:'.72rem',fontWeight:700,color:'rgba(255,255,255,.8)'}}>{label}</span>
      {sub && <span style={{fontSize:'.65rem',color:'rgba(255,255,255,.35)'}}>{sub}</span>}
    </div>
  );
}

// ── Section wrapper ───────────────────────────────────────────────────────────
function Section({ title, children, accent='#c8a96e' }) {
  return (
    <div style={{background:'rgba(255,255,255,.02)',border:'1px solid rgba(255,255,255,.07)',borderRadius:16,padding:'1.1rem 1.2rem',marginBottom:'.85rem'}}>
      <div style={{fontSize:'.68rem',fontWeight:800,color:accent,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:'.75rem'}}>{title}</div>
      {children}
    </div>
  );
}

// ── AI Chat Component ─────────────────────────────────────────────────────────
function AIChat({ onSend, scope }) {
  const [msgs,    setMsgs]    = useState([
    { role:'assistant', content: scope==='platform'
      ? "Hello! I'm your platform analytics AI. Ask me anything about your restaurants, revenue trends, system health, or any data you want to explore. I respond in the language you write in. 🌍"
      : "Hello! I'm your business analytics AI. Ask me about your orders, revenue, top products, customer trends — anything in your data. I respond in whatever language you write in. 🌍" }
  ]);
  const [input,   setInput]   = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  const SUGGESTIONS = scope === 'platform'
    ? ["What's total revenue this month?", "Which restaurants are performing best?", "ما وضعیت سیستم در ماه گذشته بوده؟", "Show platform health summary"]
    : ["What are my top selling items?", "ساعات اوج فروش من چه زمانی است؟", "How can I increase revenue?", "Summarize this week's performance"];

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:'smooth' }); }, [msgs]);

  const send = async (text) => {
    const t = text || input.trim();
    if (!t || loading) return;
    setInput('');
    const userMsg = { role:'user', content:t };
    setMsgs(m => [...m, userMsg]);
    setLoading(true);
    try {
      const history = [...msgs.filter(m=>m.role!=='system'), userMsg];
      const { reply } = await onSend(history);
      setMsgs(m => [...m, { role:'assistant', content:reply }]);
    } catch(e) {
      setMsgs(m => [...m, { role:'assistant', content:'⚠️ Error: '+e.message }]);
    }
    setLoading(false);
  };

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',minHeight:480}}>
      {/* Messages */}
      <div style={{flex:1,overflowY:'auto',display:'flex',flexDirection:'column',gap:'.6rem',padding:'.5rem 0',marginBottom:'.5rem'}}>
        {msgs.map((m,i) => (
          <div key={i} style={{display:'flex',flexDirection:'column',alignItems:m.role==='user'?'flex-end':'flex-start'}}>
            <div style={{
              maxWidth:'85%',padding:'.65rem .9rem',borderRadius:m.role==='user'?'16px 16px 4px 16px':'16px 16px 16px 4px',
              background:m.role==='user'?'rgba(200,169,110,.15)':'rgba(255,255,255,.05)',
              border:`1px solid ${m.role==='user'?'rgba(200,169,110,.3)':'rgba(255,255,255,.08)'}`,
              fontSize:'.82rem',lineHeight:1.65,color:m.role==='user'?'#e8d5a8':'rgba(255,255,255,.85)',
              whiteSpace:'pre-wrap',wordBreak:'break-word',
            }}>
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{display:'flex',alignItems:'center',gap:'.5rem',padding:'.4rem .5rem'}}>
            <div style={{display:'flex',gap:4}}>
              {[0,1,2].map(i=><span key={i} style={{width:6,height:6,borderRadius:'50%',background:'rgba(200,169,110,.5)',animation:`aiBounce .8s ease-in-out ${i*0.15}s infinite alternate`,display:'inline-block'}}/>)}
            </div>
            <span style={{fontSize:'.72rem',color:'rgba(255,255,255,.3)'}}>Analyzing your data…</span>
          </div>
        )}
        <div ref={bottomRef}/>
      </div>
      {/* Suggestions */}
      <div style={{display:'flex',gap:'.3rem',flexWrap:'wrap',marginBottom:'.5rem'}}>
        {SUGGESTIONS.map((s,i)=>(
          <button key={i} onClick={()=>send(s)} style={{
            padding:'.25rem .6rem',background:'rgba(255,255,255,.04)',border:'1px solid rgba(255,255,255,.1)',
            borderRadius:20,color:'rgba(255,255,255,.45)',fontFamily:"'DM Sans',sans-serif",
            fontSize:'.68rem',cursor:'pointer',transition:'all .2s',whiteSpace:'nowrap',
          }}>{s}</button>
        ))}
      </div>
      {/* Input */}
      <div style={{display:'flex',gap:'.5rem',alignItems:'flex-end'}}>
        <textarea
          value={input} onChange={e=>setInput(e.target.value)}
          onKeyDown={e=>{ if(e.key==='Enter'&&!e.shiftKey){ e.preventDefault(); send(); } }}
          placeholder="Ask anything in any language… (Enter to send)"
          rows={2}
          style={{
            flex:1,padding:'.65rem .85rem',background:'rgba(255,255,255,.06)',
            border:'1px solid rgba(255,255,255,.12)',borderRadius:12,color:'#fff',
            fontFamily:"'DM Sans',sans-serif",fontSize:'.85rem',resize:'none',outline:'none',
            lineHeight:1.5,
          }}
        />
        <button onClick={()=>send()} disabled={loading||!input.trim()} style={{
          padding:'.65rem 1.1rem',background:'linear-gradient(135deg,#c8a96e,#a07840)',
          border:'none',borderRadius:12,color:'#fff',fontFamily:"'DM Sans',sans-serif",
          fontSize:'.85rem',fontWeight:700,cursor:'pointer',opacity:(loading||!input.trim())?0.4:1,
          transition:'opacity .2s',whiteSpace:'nowrap',
        }}>
          {loading?'…':'Send ✦'}
        </button>
      </div>
      <style>{`@keyframes aiBounce{from{transform:translateY(0)}to{transform:translateY(-5px)}}`}</style>
    </div>
  );
}

// ── Audit Log Viewer ──────────────────────────────────────────────────────────
function AuditLogViewer({ fetchLogs, exportUrl }) {
  const [logs,    setLogs]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const [loading, setLoading] = useState(false);
  const [search,  setSearch]  = useState('');
  const [category,setCategory]= useState('');
  const [from,    setFrom]    = useState('');
  const [to,      setTo]      = useState('');

  const CATEGORIES = ['','order','product','category','customer','admin','settings','auth','payment','events','branch','restaurant','media','general'];
  const CAT_COLORS = { order:'#fbbf24', product:'#34d399', category:'#60a5fa', customer:'#a78bfa', admin:'#f87171', settings:'#94a3b8', auth:'#fb923c', payment:'#c8a96e', events:'#f472b6', branch:'#38bdf8' };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit:50 });
      if (search)   params.set('search',   search);
      if (category) params.set('category', category);
      if (from)     params.set('from',     from);
      if (to)       params.set('to',       to+'T23:59:59');
      const res = await fetchLogs(params.toString());
      setLogs(res.data || []);
      setTotal(res.pagination?.total || 0);
    } catch {}
    setLoading(false);
  }, [page, search, category, from, to, fetchLogs]);

  useEffect(() => { load(); }, [load]);

  const ACTION_ICONS = { order_placed:'📋', status_changed:'🔄', product_added:'➕', product_updated:'✏️', product_deleted:'🗑️', admin_login:'🔐', owner_login:'🔑', platform_login:'🛡️', profile_updated:'👤', payment_updated:'💳', restaurant_updated:'⚙️', subadmin_created:'👥', order_created:'📋', settings_updated:'⚙️' };

  return (
    <div>
      {/* Filters */}
      <div style={{display:'flex',gap:'.5rem',flexWrap:'wrap',marginBottom:'.75rem',alignItems:'flex-end'}}>
        <div style={{flex:2,minWidth:160}}>
          <div style={{fontSize:'.62rem',color:'rgba(255,255,255,.35)',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:'.2rem'}}>Search</div>
          <input value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}} placeholder="Email, name, action…"
            style={{width:'100%',padding:'.42rem .65rem',background:'rgba(255,255,255,.06)',border:'1px solid rgba(255,255,255,.1)',borderRadius:8,color:'#fff',fontFamily:"'DM Sans',sans-serif",fontSize:'.8rem',outline:'none',boxSizing:'border-box'}}/>
        </div>
        <div style={{minWidth:120}}>
          <div style={{fontSize:'.62rem',color:'rgba(255,255,255,.35)',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:'.2rem'}}>Category</div>
          <select value={category} onChange={e=>{setCategory(e.target.value);setPage(1);}}
            style={{padding:'.42rem .65rem',background:'rgba(255,255,255,.06)',border:'1px solid rgba(255,255,255,.1)',borderRadius:8,color:'#fff',fontFamily:"'DM Sans',sans-serif",fontSize:'.8rem',cursor:'pointer'}}>
            {CATEGORIES.map(c=><option key={c} value={c}>{c||'All Categories'}</option>)}
          </select>
        </div>
        <div>
          <div style={{fontSize:'.62rem',color:'rgba(255,255,255,.35)',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:'.2rem'}}>From</div>
          <input type="date" value={from} onChange={e=>setFrom(e.target.value)}
            style={{padding:'.42rem .65rem',background:'rgba(255,255,255,.06)',border:'1px solid rgba(255,255,255,.1)',borderRadius:8,color:'#fff',fontFamily:"'DM Sans',sans-serif",fontSize:'.8rem'}}/>
        </div>
        <div>
          <div style={{fontSize:'.62rem',color:'rgba(255,255,255,.35)',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:'.2rem'}}>To</div>
          <input type="date" value={to} onChange={e=>setTo(e.target.value)}
            style={{padding:'.42rem .65rem',background:'rgba(255,255,255,.06)',border:'1px solid rgba(255,255,255,.1)',borderRadius:8,color:'#fff',fontFamily:"'DM Sans',sans-serif",fontSize:'.8rem'}}/>
        </div>
        <button onClick={load} style={{padding:'.42rem .9rem',background:'rgba(200,169,110,.15)',border:'1px solid rgba(200,169,110,.3)',borderRadius:8,color:'#c8a96e',fontFamily:"'DM Sans',sans-serif",fontSize:'.8rem',fontWeight:700,cursor:'pointer'}}>↻ Refresh</button>
        {exportUrl && (
          <a href={exportUrl((() => { const p = new URLSearchParams(); if(from) p.set('from',from); if(to) p.set('to',to+'T23:59:59'); if(category) p.set('category',category); return p.toString(); })())}
            style={{padding:'.42rem .9rem',background:'rgba(52,211,153,.1)',border:'1px solid rgba(52,211,153,.25)',borderRadius:8,color:'#34d399',fontFamily:"'DM Sans',sans-serif",fontSize:'.8rem',fontWeight:700,cursor:'pointer',textDecoration:'none'}}>
            ⬇ Export CSV
          </a>
        )}
      </div>

      <div style={{fontSize:'.72rem',color:'rgba(255,255,255,.3)',marginBottom:'.5rem'}}>{total.toLocaleString()} events total</div>

      {/* Log rows */}
      <div style={{display:'flex',flexDirection:'column',gap:'.3rem',maxHeight:500,overflowY:'auto'}}>
        {loading && <div style={{padding:'1rem',textAlign:'center',color:'rgba(255,255,255,.3)',fontSize:'.8rem'}}>Loading…</div>}
        {!loading && logs.length === 0 && <div style={{padding:'1rem',textAlign:'center',color:'rgba(255,255,255,.2)',fontSize:'.8rem'}}>No logs found</div>}
        {logs.map(l => {
          const cc = CAT_COLORS[l.category] || '#6b7280';
          const icon = ACTION_ICONS[l.action] || '•';
          return (
            <div key={l._id} style={{display:'grid',gridTemplateColumns:'auto 1fr auto',gap:'.5rem .75rem',alignItems:'start',padding:'.5rem .65rem',background:'rgba(255,255,255,.025)',border:'1px solid rgba(255,255,255,.05)',borderRadius:8,fontSize:'.73rem'}}>
              <span style={{fontSize:'1rem',lineHeight:1}}>{icon}</span>
              <div style={{display:'flex',flexDirection:'column',gap:'.08rem'}}>
                <div style={{display:'flex',alignItems:'center',gap:'.4rem',flexWrap:'wrap'}}>
                  <span style={{padding:'.1rem .4rem',background:`${cc}18`,border:`1px solid ${cc}44`,borderRadius:5,color:cc,fontWeight:700,fontSize:'.65rem'}}>{l.category}</span>
                  <span style={{color:'rgba(255,255,255,.7)',fontWeight:600}}>{l.action}</span>
                  {l.restaurantSlug && <span style={{color:'rgba(255,255,255,.3)'}}>@ {l.restaurantSlug}</span>}
                </div>
                <div style={{color:'rgba(255,255,255,.4)',display:'flex',gap:'.5rem',flexWrap:'wrap'}}>
                  <span>{l.actorEmail || l.actorType}</span>
                  {l.actorRole && <span style={{color:'rgba(255,255,255,.25)'}}>({l.actorRole})</span>}
                  {l.ip && <span>·  {l.ip}</span>}
                </div>
                {l.detail && Object.keys(l.detail).length > 0 && (
                  <details style={{cursor:'pointer'}}>
                    <summary style={{fontSize:'.65rem',color:'rgba(255,255,255,.2)',listStyle:'none'}}>▶ detail</summary>
                    <pre style={{fontSize:'.65rem',color:'rgba(255,255,255,.4)',background:'rgba(0,0,0,.3)',padding:'.3rem .5rem',borderRadius:5,marginTop:'.2rem',overflow:'auto',maxHeight:80}}>{JSON.stringify(l.detail,null,2)}</pre>
                  </details>
                )}
              </div>
              <div style={{color:'rgba(255,255,255,.25)',fontSize:'.65rem',whiteSpace:'nowrap',textAlign:'right'}}>
                {new Date(l.createdAt).toLocaleString(undefined,{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'})}
                {!l.success && <div style={{color:'#f87171',marginTop:'.1rem'}}>✕ failed</div>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {total > 50 && (
        <div style={{display:'flex',justifyContent:'center',gap:'.4rem',marginTop:'.75rem',flexWrap:'wrap'}}>
          {page>1 && <button onClick={()=>setPage(p=>p-1)} style={{padding:'.3rem .7rem',background:'rgba(255,255,255,.06)',border:'1px solid rgba(255,255,255,.1)',borderRadius:7,color:'rgba(255,255,255,.6)',cursor:'pointer',fontSize:'.78rem'}}>← Prev</button>}
          <span style={{padding:'.3rem .7rem',color:'rgba(255,255,255,.4)',fontSize:'.78rem'}}>Page {page} of {Math.ceil(total/50)}</span>
          {page<Math.ceil(total/50) && <button onClick={()=>setPage(p=>p+1)} style={{padding:'.3rem .7rem',background:'rgba(255,255,255,.06)',border:'1px solid rgba(255,255,255,.1)',borderRadius:7,color:'rgba(255,255,255,.6)',cursor:'pointer',fontSize:'.78rem'}}>Next →</button>}
        </div>
      )}
    </div>
  );
}

// ── Main AnalyticsPanel ───────────────────────────────────────────────────────
export default function AnalyticsPanel({ slug, scope='restaurant', fetchData, fetchLogs, fetchBehavior, aiAnalyze, currencyCode='USD', exportAuditUrl }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [days,    setDays]    = useState(30);
  const [view,    setView]    = useState('overview'); // overview | behavior | products | activity | ai | logs
  const [behavior,    setBehavior]    = useState(null);
  const [behLoading,  setBehLoading]  = useState(false);

  const fmt = useCallback((n) => {
    if (n == null) return '—';
    try { return new Intl.NumberFormat('en',{ style:'currency', currency:currencyCode, minimumFractionDigits:0 }).format(n); }
    catch { return `${currencyCode} ${Number(n).toLocaleString()}`; }
  }, [currencyCode]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchData(days);
      setData(res.data || res);
    } catch(e) { console.error('[analytics]',e); }
    setLoading(false);
  }, [fetchData, days]);

  useEffect(() => { load(); }, [load]);

  const loadBehavior = useCallback(async () => {
    if (!fetchBehavior) return;
    setBehLoading(true);
    try { const res = await fetchBehavior(days); setBehavior(res.data || res); }
    catch (e) { console.error('[behavior]', e); }
    setBehLoading(false);
  }, [fetchBehavior, days]);

  useEffect(() => { if (view === 'behavior') loadBehavior(); }, [view, loadBehavior]);

  const VIEWS = [
    { id:'overview',  icon:'📊', label:'Overview'   },
    ...(fetchBehavior ? [{ id:'behavior', icon:'🧭', label:'Behavior' }] : []),
    { id:'products',  icon:'🏆', label:'Products'   },
    { id:'activity',  icon:'⏱️', label:'Activity'   },
    { id:'ai',        icon:'✦',  label:'AI Analyst' },
    { id:'logs',      icon:'📋', label:'Audit Logs' },
  ];

  return (
    <div style={{fontFamily:"'DM Sans',sans-serif"}}>
      {/* View tabs + period selector */}
      <div style={{display:'flex',alignItems:'center',gap:'.5rem',marginBottom:'1rem',flexWrap:'wrap',justifyContent:'space-between'}}>
        <div style={{display:'flex',gap:'.3rem',flexWrap:'wrap'}}>
          {VIEWS.map(v=>(
            <button key={v.id} onClick={()=>setView(v.id)} style={{
              padding:'.38rem .85rem',borderRadius:20,border:'none',fontFamily:"'DM Sans',sans-serif",fontSize:'.78rem',fontWeight:view===v.id?700:500,cursor:'pointer',transition:'all .2s',
              background:view===v.id?'rgba(200,169,110,.2)':'rgba(255,255,255,.05)',
              color:view===v.id?'#c8a96e':'rgba(255,255,255,.45)',
            }}>{v.icon} {v.label}</button>
          ))}
        </div>
        <div style={{display:'flex',alignItems:'center',gap:'.4rem'}}>
          <span style={{fontSize:'.72rem',color:'rgba(255,255,255,.35)'}}>Period:</span>
          {[7,14,30,90].map(d=>(
            <button key={d} onClick={()=>setDays(d)} style={{
              padding:'.25rem .55rem',borderRadius:8,border:'none',fontFamily:"'DM Sans',sans-serif",fontSize:'.72rem',cursor:'pointer',
              background:days===d?'rgba(200,169,110,.2)':'rgba(255,255,255,.05)',
              color:days===d?'#c8a96e':'rgba(255,255,255,.35)',fontWeight:days===d?700:400,
            }}>{d}d</button>
          ))}
          <button onClick={load} style={{padding:'.25rem .55rem',borderRadius:8,border:'1px solid rgba(255,255,255,.1)',background:'none',color:'rgba(255,255,255,.4)',cursor:'pointer',fontSize:'.72rem'}}>↻</button>
        </div>
      </div>

      {loading && (
        <div style={{display:'flex',alignItems:'center',justifyContent:'center',padding:'3rem',gap:'.75rem',color:'rgba(255,255,255,.3)'}}>
          <div style={{width:20,height:20,border:'2px solid rgba(200,169,110,.2)',borderTopColor:'#c8a96e',borderRadius:'50%',animation:'analSpin .8s linear infinite'}}/>
          Loading analytics…
          <style>{`@keyframes analSpin{to{transform:rotate(360deg)}}`}</style>
        </div>
      )}

      {!loading && data && view === 'overview' && (
        <div>
          {/* KPI Cards */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:'.6rem',marginBottom:'1rem'}}>
            <StatCard icon="💰" label="Revenue" value={fmt(data.totals?.revenue||data.totalRevenue||0)} sub={`Last ${days} days`} color="#c8a96e"/>
            <StatCard icon="📋" label="Orders"  value={(data.totals?.orders||data.totalOrders||0).toLocaleString()} sub={`${data.todayOrders||0} today`} color="#60a5fa"/>
            <StatCard icon="📊" label="Avg Order" value={fmt(data.totals?.avgOrder||0)} color="#34d399"/>
            <StatCard icon="👥" label="Customers" value={(data.totalCustomers||0).toLocaleString()} color="#a78bfa"/>
            {data.todayRevenue != null && <StatCard icon="🌟" label="Today Revenue" value={fmt(data.todayRevenue)} color="#f97316"/>}
            {data.activeRestaurants != null && <StatCard icon="🍽️" label="Active Restaurants" value={data.activeRestaurants} sub={`of ${data.totalRestaurants}`} color="#34d399"/>}
          </div>

          {/* Revenue over time */}
          {data.daily?.length > 0 && (
            <Section title={`Revenue — Last ${days} Days`}>
              <LineChart data={data.daily||data.dailyOrders||[]} color="#c8a96e" height={130} valueKey="revenue" labelKey="_id"/>
              <div style={{marginTop:'.5rem'}}>
                <LineChart data={data.daily||data.dailyOrders||[]} color="#60a5fa" height={80} valueKey="orders" labelKey="_id"/>
              </div>
              <div style={{fontSize:'.65rem',color:'rgba(255,255,255,.25)',marginTop:'.25rem',display:'flex',gap:'1.5rem'}}>
                <span style={{color:'#c8a96e'}}>● Revenue</span>
                <span style={{color:'#60a5fa'}}>● Orders</span>
              </div>
            </Section>
          )}

          {/* Distributions */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'.6rem',marginBottom:'.85rem'}}>
            {data.statusDist?.length > 0 && (
              <Section title="Order Status">
                <DonutChart data={data.statusDist} valueKey="count" labelKey="_id"/>
              </Section>
            )}
            {(data.paymentDist?.length > 0) && (
              <Section title="Payment Methods">
                <DonutChart data={data.paymentDist} valueKey="count" labelKey="_id"/>
              </Section>
            )}
            {data.typeDist?.length > 0 && (
              <Section title="Order Types">
                <DonutChart data={data.typeDist} valueKey="count" labelKey="_id"/>
              </Section>
            )}
            {data.auditBreakdown?.length > 0 && (
              <Section title="Activity Categories">
                <DonutChart data={data.auditBreakdown} valueKey="count" labelKey="_id"/>
              </Section>
            )}
          </div>

          {/* Peak hours heatmap */}
          {data.hourly?.length > 0 && (
            <Section title="Peak Hours Heatmap">
              <HeatmapRow data={data.hourly}/>
              <div style={{fontSize:'.65rem',color:'rgba(255,255,255,.25)',marginTop:'.4rem'}}>Darker = more orders. Numbers show hour of day (0–23)</div>
            </Section>
          )}
        </div>
      )}

      {!loading && data && view === 'products' && (
        <div>
          {data.topProducts?.length > 0 ? (
            <>
              <Section title="Top Products by Quantity Sold">
                <BarChart data={data.topProducts} color="#c8a96e" height={140} valueKey="totalQty" labelKey="_id"/>
              </Section>
              <Section title="Top Products by Revenue">
                <BarChart data={data.topProducts} color="#34d399" height={140} valueKey="totalRev" labelKey="_id"/>
              </Section>
              <Section title="Product Details">
                <div style={{display:'flex',flexDirection:'column',gap:'.35rem'}}>
                  {data.topProducts.map((p,i)=>(
                    <div key={i} style={{display:'flex',alignItems:'center',gap:'.75rem',padding:'.45rem .65rem',background:'rgba(255,255,255,.04)',borderRadius:9}}>
                      <span style={{width:22,height:22,borderRadius:'50%',background:'rgba(200,169,110,.15)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'.72rem',fontWeight:800,color:'#c8a96e',flexShrink:0}}>#{i+1}</span>
                      <span style={{flex:1,fontSize:'.8rem',fontWeight:600,color:'#fff'}}>{p._id}</span>
                      <span style={{fontSize:'.72rem',color:'#60a5fa'}}>×{p.totalQty}</span>
                      <span style={{fontSize:'.75rem',fontWeight:700,color:'#c8a96e'}}>{fmt(p.totalRev)}</span>
                    </div>
                  ))}
                </div>
              </Section>
            </>
          ) : (
            <div style={{padding:'2rem',textAlign:'center',color:'rgba(255,255,255,.25)'}}>No product data for this period</div>
          )}
          {data.topRestaurants?.length > 0 && (
            <Section title="Top Restaurants by Revenue">
              <BarChart data={data.topRestaurants} color="#a78bfa" height={130} valueKey="revenue" labelKey="_id"/>
              <div style={{display:'flex',flexDirection:'column',gap:'.3rem',marginTop:'.6rem'}}>
                {data.topRestaurants.map((r,i)=>(
                  <div key={i} style={{display:'flex',alignItems:'center',gap:'.75rem',padding:'.4rem .65rem',background:'rgba(255,255,255,.04)',borderRadius:9}}>
                    <span style={{width:20,height:20,borderRadius:'50%',background:'rgba(167,139,250,.15)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'.68rem',fontWeight:800,color:'#a78bfa',flexShrink:0}}>#{i+1}</span>
                    <span style={{flex:1,fontSize:'.78rem',fontWeight:600,color:'#fff'}}>{r._id}</span>
                    <span style={{fontSize:'.7rem',color:'rgba(255,255,255,.4)'}}>orders: {r.orders}</span>
                    <span style={{fontSize:'.75rem',fontWeight:700,color:'#a78bfa'}}>{fmt(r.revenue)}</span>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>
      )}

      {!loading && data && view === 'activity' && (
        <div>
          {data.hourly?.length > 0 && (
            <Section title="Hourly Order Pattern">
              <HeatmapRow data={data.hourly} label="Orders by Hour"/>
              <BarChart data={data.hourly} color="#f97316" height={100} valueKey="count" labelKey="_id"/>
            </Section>
          )}
          {data.recentActivity?.length > 0 && (
            <Section title="Recent System Events">
              <div style={{display:'flex',flexDirection:'column',gap:'.3rem',maxHeight:380,overflowY:'auto'}}>
                {data.recentActivity.map((l,i)=>(
                  <div key={i} style={{display:'flex',gap:'.6rem',padding:'.4rem .6rem',background:'rgba(255,255,255,.03)',borderRadius:8,alignItems:'flex-start'}}>
                    <span style={{width:28,height:28,borderRadius:'50%',background:'rgba(255,255,255,.06)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'.75rem',flexShrink:0}}>
                      {l.actorType==='customer'?'👤':l.actorType==='admin'?'🔑':l.actorType==='owner'?'👔':'🌐'}
                    </span>
                    <div style={{flex:1}}>
                      <div style={{fontSize:'.78rem',color:'rgba(255,255,255,.7)'}}><strong>{l.action}</strong> <span style={{color:'rgba(255,255,255,.35)'}}>by {l.actorEmail||l.actorType}</span></div>
                      {l.restaurantSlug && <div style={{fontSize:'.67rem',color:'rgba(255,255,255,.25)'}}>{l.restaurantSlug}</div>}
                    </div>
                    <span style={{fontSize:'.65rem',color:'rgba(255,255,255,.2)',whiteSpace:'nowrap'}}>{new Date(l.createdAt).toLocaleString(undefined,{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'})}</span>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>
      )}

      {view === 'behavior' && (
        <div>
          {behLoading && (
            <div style={{display:'flex',alignItems:'center',justifyContent:'center',padding:'3rem',gap:'.75rem',color:'rgba(255,255,255,.3)'}}>
              <div style={{width:20,height:20,border:'2px solid rgba(200,169,110,.2)',borderTopColor:'#c8a96e',borderRadius:'50%',animation:'analSpin .8s linear infinite'}}/>
              Loading behaviour…
            </div>
          )}
          {!behLoading && behavior && (
            <>
              {/* KPI cards */}
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:'.6rem',marginBottom:'1rem'}}>
                <StatCard icon="👣" label="Unique Visitors" value={(behavior.uniqueVisitors||0).toLocaleString()} sub={`Last ${days} days`} color="#60a5fa"/>
                <StatCard icon="🧭" label="Sessions" value={(behavior.sessions||0).toLocaleString()} color="#a78bfa"/>
                <StatCard icon="🎯" label="Visit→Order" value={`${behavior.conversionRate||0}%`} sub="conversion" color="#34d399"/>
                <StatCard icon="🛒" label="Cart→Order" value={`${behavior.cartConversion||0}%`} color="#c8a96e"/>
                <StatCard icon="🚪" label="Cart Abandon" value={`${behavior.cartAbandonment||0}%`} color="#f87171"/>
                <StatCard icon="📈" label="Events" value={(behavior.totalEvents||0).toLocaleString()} color="#f97316"/>
              </div>

              {/* Conversion funnel */}
              <Section title="Conversion Funnel">
                <div style={{display:'flex',flexDirection:'column',gap:'.5rem'}}>
                  {(behavior.funnel||[]).map((s,i)=>{
                    const COLORS=['#60a5fa','#a78bfa','#c8a96e','#f97316','#34d399'];
                    const c=COLORS[i%COLORS.length];
                    return (
                      <div key={i} style={{display:'flex',alignItems:'center',gap:'.6rem'}}>
                        <span style={{width:104,fontSize:'.74rem',color:'rgba(255,255,255,.6)',flexShrink:0}}>{s.step}</span>
                        <div style={{flex:1,height:24,background:'rgba(255,255,255,.04)',borderRadius:7,overflow:'hidden'}}>
                          <div style={{width:`${Math.max(s.rate,2)}%`,height:'100%',background:c,opacity:.85,borderRadius:7,transition:'width .4s',display:'flex',alignItems:'center',paddingLeft:8}}>
                            <span style={{fontSize:'.68rem',fontWeight:700,color:'#111'}}>{s.rate}%</span>
                          </div>
                        </div>
                        <span style={{width:64,textAlign:'right',fontSize:'.72rem',fontWeight:700,color:c,flexShrink:0}}>{(s.sessions||0).toLocaleString()}</span>
                      </div>
                    );
                  })}
                </div>
                <div style={{fontSize:'.65rem',color:'rgba(255,255,255,.25)',marginTop:'.5rem'}}>% of all sessions that reached each step.</div>
              </Section>

              {/* Most viewed + searches/devices */}
              <Section title="Most Viewed Products (view → add-to-cart)">
                {behavior.topViewed?.length ? (
                  <div style={{display:'flex',flexDirection:'column',gap:'.35rem'}}>
                    {behavior.topViewed.map((p,i)=>(
                      <div key={i} style={{display:'flex',alignItems:'center',gap:'.6rem',padding:'.45rem .65rem',background:'rgba(255,255,255,.04)',borderRadius:9}}>
                        <span style={{width:22,height:22,borderRadius:'50%',background:'rgba(96,165,250,.15)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'.7rem',fontWeight:800,color:'#60a5fa',flexShrink:0}}>#{i+1}</span>
                        <span style={{flex:1,fontSize:'.8rem',fontWeight:600,color:'#fff',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{p.name}</span>
                        <span style={{fontSize:'.7rem',color:'#60a5fa'}}>👁 {p.views}</span>
                        <span style={{fontSize:'.7rem',color:'#c8a96e'}}>🛒 {p.carts}</span>
                        <span style={{fontSize:'.72rem',fontWeight:700,color:p.viewToCart>=20?'#34d399':'rgba(255,255,255,.5)'}}>{p.viewToCart}%</span>
                      </div>
                    ))}
                  </div>
                ) : <div style={{padding:'1.5rem',textAlign:'center',color:'rgba(255,255,255,.25)',fontSize:'.8rem'}}>No product views yet for this period</div>}
              </Section>

              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'.6rem',marginBottom:'.85rem'}}>
                <Section title="Top Searches">
                  {behavior.topSearches?.length ? (
                    <div style={{display:'flex',flexDirection:'column',gap:'.3rem'}}>
                      {behavior.topSearches.map((s,i)=>(
                        <div key={i} style={{display:'flex',alignItems:'center',gap:'.5rem',fontSize:'.78rem'}}>
                          <span style={{color:'rgba(255,255,255,.3)',width:16}}>{i+1}.</span>
                          <span style={{flex:1,color:'rgba(255,255,255,.75)'}}>"{s.query}"</span>
                          <span style={{color:'#a78bfa',fontWeight:700}}>{s.count}</span>
                        </div>
                      ))}
                    </div>
                  ) : <div style={{padding:'1rem',textAlign:'center',color:'rgba(255,255,255,.25)',fontSize:'.78rem'}}>No searches yet</div>}
                </Section>
                <Section title="Devices">
                  {behavior.devices?.length ? <DonutChart data={behavior.devices} valueKey="count" labelKey="device"/>
                    : <div style={{padding:'1rem',textAlign:'center',color:'rgba(255,255,255,.25)',fontSize:'.78rem'}}>No data</div>}
                </Section>
              </div>

              {/* Daily visits + browse hours */}
              {behavior.daily?.length > 0 && (
                <Section title={`Visits & Product Views — Last ${days} Days`}>
                  <LineChart data={behavior.daily} color="#60a5fa" height={120} valueKey="visits" labelKey="date"/>
                  <div style={{marginTop:'.4rem'}}>
                    <LineChart data={behavior.daily} color="#c8a96e" height={70} valueKey="views" labelKey="date"/>
                  </div>
                  <div style={{fontSize:'.65rem',color:'rgba(255,255,255,.25)',marginTop:'.25rem',display:'flex',gap:'1.5rem'}}>
                    <span style={{color:'#60a5fa'}}>● Visits</span><span style={{color:'#c8a96e'}}>● Product views</span>
                  </div>
                </Section>
              )}
              {behavior.hourly?.length > 0 && (
                <Section title="Busiest Browse Hours">
                  <HeatmapRow data={behavior.hourly} label="Visits by Hour"/>
                </Section>
              )}
            </>
          )}
          {!behLoading && !behavior && (
            <div style={{padding:'2rem',textAlign:'center',color:'rgba(255,255,255,.25)'}}>No behaviour data yet. It appears as customers browse your menu.</div>
          )}
        </div>
      )}

      {view === 'ai' && (
        <Section title="✦ AI Business Analyst" accent="#c8a96e">
          <AIChat onSend={aiAnalyze} scope={scope}/>
        </Section>
      )}

      {view === 'logs' && fetchLogs && (
        <Section title="📋 Full Audit Log" accent="#60a5fa">
          <AuditLogViewer fetchLogs={fetchLogs} exportUrl={exportAuditUrl}/>
        </Section>
      )}
    </div>
  );
}
